import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useAtomValue } from 'jotai';
import SubjectIcon from '@mui/icons-material/Subject';
import useAPI from '../../../api';
import WidgetBox from '../../../Components/Paper/WidgetBox';
import ActivityHistoryList from '../../../Components/Activities/ActivityHistoryList';
import CallNotesDialog from './CallNotesDialog';
import EmailDetailsWidget from '../EmailSection/EmailDetailsWidget';
import HistoryFilters from '../Shared/HistoryFilters';
import Dialog from '../../../Components/Dialog/index';
import {
  notifyEndCallAtom,
  notifyUpdateCallAtom,
} from '../../../Atoms/HubAtoms';
import { getCallAnsweredState } from '../../../Helpers/calls';

const HistorySection = function HistorySection({
  customerId,
  campaignId,
  filterHistory,
  setFilterHistory,
  handleReply,
  infiniteQueryData,
  selectedCustomer,
  userRoles,
}) {
  const intl = useIntl();
  const API = useAPI();

  const isOperator = Boolean(
    userRoles?.find(
      ({ roleId }) => roleId === 'OPERATOR' || roleId === 'LIMITEDOPERATOR'
    )
  );
  const notifyEndCall = useAtomValue(notifyEndCallAtom);
  const notifyUpdateCall = useAtomValue(notifyUpdateCallAtom);
  const [detailsDialog, setDetailsDialog] = useState(null);

  //patch a call already shown in the history with its latest state (answered/outcome)
  const updateHistoryCallState = (call) => {
    const existing = infiniteQueryData.items?.find(
      ({ id }) => id === call?.id
    );
    if (!existing) return;
    infiniteQueryData.updateItem({
      ...existing,
      callStatus: call.callStatus ?? existing.callStatus,
      connectedOn: call.connectedOn ?? existing.connectedOn,
      disconnectedOn: call.disconnectedOn ?? existing.disconnectedOn,
      answered: getCallAnsweredState(call),
      outcome: call.outcome ?? existing.outcome,
      completedOn: call.completedOn ?? existing.completedOn,
    });
  };

  const handleOpenDetailsDialog = (value) => {
    setDetailsDialog(value);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialog(null);
  };

  const handleSelectFilter = (e, newTypes) => {
    if (newTypes !== null) setFilterHistory(newTypes);
  };

  const handleEditNotes = (notesObj) => {
    API.putNotes(
      notesObj,
      () => {
        infiniteQueryData.reload();
      },
      () => {}
    );
  };

  useEffect(() => {
    //live call update: sync the matching history call state if it is already listed
    if (notifyUpdateCall && notifyUpdateCall?.baseEntityId === customerId) {
      updateHistoryCallState(notifyUpdateCall);
    }
  }, [notifyUpdateCall]);

  useEffect(() => {
    if (notifyEndCall && notifyEndCall?.baseEntityId === customerId) {
      //after call outcome is setted reloads activities
      if (notifyEndCall.outcome) {
        infiniteQueryData.reload();
      } else {
        //no outcome yet: just sync the call state of the listed activity
        updateHistoryCallState(notifyEndCall);
      }
    }
  }, [notifyEndCall]);

  return (
    <>
      <WidgetBox
        flex="1 1 33%"
        title={intl.messages.ACTIVITY_HISTORY}
        widget={
          <ActivityHistoryList
            selectedCustomer={selectedCustomer}
            campaignId={campaignId}
            openReply={handleReply}
            clientPage //check if the list is in ClientPage for styling and spread different data
            ObserverWrapper={infiniteQueryData.ObserverWrapper}
            handleOpenDetailsDialog={handleOpenDetailsDialog}
            isOperator={isOperator}
          />
        }
        titleWidget={
          <HistoryFilters
            selectedFilter={filterHistory}
            handleSelectFilter={handleSelectFilter}
          />
        }
      />
      <CallNotesDialog
        handleEditNotes={handleEditNotes}
        open={Boolean(
          detailsDialog?.activityId && detailsDialog?.activityType !== 'EMAIL'
        )}
        disableEdit={
          detailsDialog?.activityType === 'SMS' ||
          detailsDialog?.activityType === 'EMAIL'
        }
        close={handleCloseDetailsDialog}
        id={
          infiniteQueryData.items?.find(
            ({ id }) => id === detailsDialog?.activityId
          )?.id
        }
        notesTxt={
          infiniteQueryData.items?.find(
            ({ id }) => id === detailsDialog?.activityId
          )?.content ||
          infiniteQueryData.items?.find(
            ({ id }) => id === detailsDialog?.activityId
          )?.notes
        }
      />
      <Dialog
        id="email-dialog"
        open={
          detailsDialog?.activityId && detailsDialog?.activityType === 'EMAIL'
        }
        onClose={handleCloseDetailsDialog}
        fullScreen
        icon={<SubjectIcon />}
        title={
          infiniteQueryData.items?.find(
            ({ id }) => id === detailsDialog?.activityId
          )?.subject || intl.messages.EMAIL_DETAILS
        }
      >
        <EmailDetailsWidget
          handleReply={handleReply}
          emailData={infiniteQueryData.items?.find(
            ({ id }) => id === detailsDialog?.activityId
          )}
        />
      </Dialog>
    </>
  );
};

HistorySection.propTypes = {
  customerId: PropTypes.string,
  handleReply: () => {}, //openReply
};
export default HistorySection;
